import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslations } from "use-intl";

import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import { FlightsList } from "@/features/flights/components/flight-list/FlightsList";
import { SelectedDepartureFlightDrawer } from "@/features/flights/components/flight-list/SelectedDepartureFlightDrawer";
import { useFlightFiltersContext } from "@/features/flights/contexts/FlightFiltersContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { useReturnFlightSearch } from "@/features/flights/hooks/flightSearch/useReturnFlightSearch";
import {
  useFlightQuickSearchTokens,
  useFlightSearchTokens,
} from "@/features/flights/hooks/useFlightSearchTokens";
import type {
  FlightType,
  MappedFlightOffer,
} from "@/features/flights/types/types";

export const Route = createFileRoute(
  "/_authenticated/flights/_flights/return/list",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { history } = useRouter();
  const t = useTranslations();

  const {
    handleReturnFlightSelection,
    selectedOfferIndex,
    selectedDepartureFlightId,
    selectedFlightSearchSource,
  } = useFlightSelectionPayloadContext();
  const flightSearch = useFlightSearchTokens({
    enabled: selectedFlightSearchSource === "full",
  });
  const quickTokensQuery = useFlightQuickSearchTokens();
  const { filters } = useFlightFiltersContext();
  const searchQuery = useReturnFlightSearch(
    selectedFlightSearchSource === "quick"
      ? quickTokensQuery
      : flightSearch.query,
    filters,
    selectedOfferIndex,
    selectedDepartureFlightId,
  );

  const handleFlightSelect = (offer: MappedFlightOffer, flight: FlightType) => {
    handleReturnFlightSelection(offer, flight.key);
    navigate({
      to: "/flights/offer/$id/step/1",
      params: { id: offer.index },
    });
  };

  return (
    <main className="no-footer main h-screen pb-[calc(var(--spacing)*2+var(--departure-flight-drawer-height,0))]">
      <FlightsList
        onFlightSelect={handleFlightSelect}
        direction="return"
        searchQuery={{ query: searchQuery, type: selectedFlightSearchSource }}
        title={<div>{t("flights.chooseReturnFlight")}</div>}
        // when user triggers new search he has to select departure flight again so we need to navigate back to departure flights list
        onAfterSearchSubmit={() => history.back()}
      />
      <SelectedDepartureFlightDrawer />
      <WhatsAppButton
        bottomPosition={
          "calc(var(--departure-flight-drawer-height) + calc(var(--spacing)*4))"
        }
      />
    </main>
  );
}
